import React, { useState, useEffect, useRef } from 'react';
import { Pencil, Trash2, Copy, Check, X, Key, LogOut, Mail, Plus, ClipboardList, BookOpen, UserMinus, ChevronDown, ChevronUp, ChevronRight, RefreshCw, ShieldCheck, ShieldOff, AlertTriangle, Camera, Monitor, Smartphone, Building2, UserPlus, HelpCircle, Users, Megaphone, Heart } from 'lucide-react';
import packageJson from '../../package.json';
import { useAuth } from '../store/auth';
import { useGroups } from '../store/groups';
import { useInventory } from '../store/inventory';
import { useConsent } from '../store/consent';
import { useOrganizations } from '../store/organizations';
import { Avatar, Badge, Card, BottomSheet, PrimaryBtn, SecondaryBtn, Field, Input, Select, Textarea, SectionLabel, Divider, ErrorMsg, useConfirm } from '../components/UI';
import { FYLKER, KOMMUNER_BY_FYLKE } from '../lib/norwayGeo';
import { isValidGroupName, isValidName, isValidEmail, isValidInviteCode, isValidPassword } from '../lib/validation';
import { compressImage, deleteStorageImage } from '../lib/imageUtils';
import { HelpPage } from './HelpPage';
import { PersonvernPage } from './PersonvernPage';
import { supabase } from '../lib/supabase';
import { Organization } from '../types';

type ProfileTab = 'profil' | 'grupper' | 'sikkerhet';

export const ProfilePage: React.FC<{ onJoined: () => void; onOpenFeedback: () => void }> = ({ onJoined, onOpenFeedback }) => {
  const [activeProfileTab, setActiveProfileTab] = useState<ProfileTab>('profil');
  const { user, profile, signOut, updateProfile, deleteAccount, enrollMfa, confirmMfaEnrollment, unenrollMfa, getMfaStatus, changePassword } = useAuth();
  const { groups, currentGroupId, currentGroup, members, tools, loanHistory, groupInvites, loading: groupsLoading, setCurrentGroup, createGroup, joinByCode, updateGroup, removeMember, updateMemberRole, fetchLoanHistory, fetchMembers, sendInviteEmail, deleteGroup, leaveGroup, removeToolFromGroup, fetchGroupInvites, deleteGroupInvite } = useGroups();
  const { loanableItems, fetchLoanableItems } = useInventory();
  const { adConsent, reopenBanner, sponsorConsent, setSponsorConsent } = useConsent();
  const {
    organizations, myAdminOrgIds, orgAdminsList, orgMembersList, orgGroups, myAdminGroups, groupOrgInfo,
    fetchMyOrganizations, createOrganization, joinOrganizationByCode,
    fetchOrgAdminsList, addOrgAdmin, removeOrgAdmin, fetchOrgMembersList, leaveOrganization, removeOrgMember,
    fetchOrgGroups, fetchMyAdminGroups, linkGroupToOrganization, unlinkGroupFromOrganization,
    deleteOrganization, updateOrganizationType, fetchGroupOrganization,
  } = useOrganizations();

  const [showGroupSheet, setShowGroupSheet] = useState(false);
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [showJoinSheet, setShowJoinSheet] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [showDeleteGroup, setShowDeleteGroup] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [deleteGroupError, setDeleteGroupError] = useState('');
  const [deleteGroupConfirmed, setDeleteGroupConfirmed] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showInviteEmail, setShowInviteEmail] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSent, setInviteSent] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupDesc, setGroupDesc] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [newFullName, setNewFullName] = useState(profile?.full_name || '');
  const [newKommune, setNewKommune] = useState(profile?.kommune || '');
  const [newFylke, setNewFylke] = useState(profile?.fylke || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showRemoveMember, setShowRemoveMember] = useState(false);
  const [showLeaveGroup, setShowLeaveGroup] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  const [leaving, setLeaving] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{id: string, userId: string, name: string} | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showPersonvern, setShowPersonvern] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [invitesOpen, setInvitesOpen] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);

  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [showMoreAvatars, setShowMoreAvatars] = useState(false);

  const [showCreateOrgSheet, setShowCreateOrgSheet] = useState(false);
  const [showJoinOrgSheet, setShowJoinOrgSheet] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('');
  const [orgCreateError, setOrgCreateError] = useState('');
  const [orgCreating, setOrgCreating] = useState(false);
  const [joinOrgCode, setJoinOrgCode] = useState('');
  const [joinOrgError, setJoinOrgError] = useState('');
  const [joinOrgSaving, setJoinOrgSaving] = useState(false);
  const [manageOrgId, setManageOrgId] = useState<string | null>(null);
  const [newOrgAdminEmail, setNewOrgAdminEmail] = useState('');
  const [orgAdminError, setOrgAdminError] = useState('');
  const [orgAddingAdmin, setOrgAddingAdmin] = useState(false);
  const [linkGroupId, setLinkGroupId] = useState('');
  const [linkGroupError, setLinkGroupError] = useState('');
  const [showGroupHelpText, setShowGroupHelpText] = useState(false);
  const [showCreateOrgGroup, setShowCreateOrgGroup] = useState(false);
  const [newOrgGroupName, setNewOrgGroupName] = useState('');
  const [newOrgGroupError, setNewOrgGroupError] = useState('');
  const [orgTypeSaved, setOrgTypeSaved] = useState(false);
  const [newOrgGroupSaving, setNewOrgGroupSaving] = useState(false);
  const [orgCopied, setOrgCopied] = useState<string | null>(null);

  const { confirm: confirmOrg, dialog: orgConfirmDialog } = useConfirm();

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSaving, setChangePasswordSaving] = useState(false);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (!file.type.startsWith('image/')) { setAvatarError('Kun bildefiler støttes'); return; }
    if (file.size > 10 * 1024 * 1024) { setAvatarError('Filen er for stor (maks 10 MB)'); return; }
    setAvatarUploading(true); setAvatarError('');
    const compressed = await compressImage(file, 400, 0.8);
    const path = `avatars/${user.id}.jpg`;
    const { error: upErr } = await supabase.storage.from('tool-images').upload(path, compressed, { upsert: true });
    if (upErr) { setAvatarError(`Opplasting feilet: ${upErr.message}`); setAvatarUploading(false); return; }
    const { data: { publicUrl } } = supabase.storage.from('tool-images').getPublicUrl(path);
    await updateProfile({ avatar_url: publicUrl });
    setAvatarUploading(false);
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  const handleDeleteAvatar = async () => {
    if (!user) return;
    setAvatarUploading(true); setAvatarError('');
    await deleteStorageImage(profile?.avatar_url);
    await updateProfile({ avatar_url: null });
    setAvatarUploading(false);
  };

  type TrustedDevice = { id: string; device_name: string; last_used_at: string; created_at: string };
  const [trustedDevices, setTrustedDevices] = useState<TrustedDevice[]>([]);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const currentDeviceId = user ? localStorage.getItem(`ts_device_id_${user.id}`) : null;

  useEffect(() => {
    if (!user) return;
    supabase.from('trusted_devices').select('id, device_name, last_used_at, created_at')
      .eq('user_id', user.id).order('last_used_at', { ascending: false })
      .then(({ data }) => setTrustedDevices(data || []));
  }, [user]);

  const revokeDevice = async (id: string) => {
    await supabase.from('trusted_devices').delete().eq('id', id);
    if (id === currentDeviceId && user) {
      localStorage.removeItem(`ts_token_${user.id}`);
      localStorage.removeItem(`ts_device_id_${user.id}`);
    }
    setTrustedDevices(prev => prev.filter(d => d.id !== id));
  };

  const revokeAllDevices = async () => {
    if (!user) return;
    await supabase.from('trusted_devices').delete().eq('user_id', user.id);
    localStorage.removeItem(`ts_token_${user.id}`);
    localStorage.removeItem(`ts_device_id_${user.id}`);
    setTrustedDevices([]);
  };

  const formatRelative = (iso: string) => {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const days = Math.floor(diff / 86400000);
    const time = d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
    if (days === 0) return `i dag kl. ${time}`;
    if (days === 1) return `i går kl. ${time}`;
    if (days < 7) return `for ${days} dager siden`;
    if (days < 30) return `for ${Math.floor(days / 7)} uker siden`;
    return `for ${Math.floor(days / 30)} mnd siden`;
  };

  const [mfaEnrolled, setMfaEnrolled] = useState(false);
  const [mfaActiveFactorId, setMfaActiveFactorId] = useState<string | null>(null);
  const [showMfaEnroll, setShowMfaEnroll] = useState(false);
  const [mfaQr, setMfaQr] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaEnrollFactorId, setMfaEnrollFactorId] = useState('');
  const [mfaConfirmCode, setMfaConfirmCode] = useState('');
  const [mfaConfirmError, setMfaConfirmError] = useState('');
  const [mfaConfirming, setMfaConfirming] = useState(false);
  const [mfaEnrolling, setMfaEnrolling] = useState(false);
  const [showMfaUnenroll, setShowMfaUnenroll] = useState(false);
  const [mfaUnenrolling, setMfaUnenrolling] = useState(false);
  const [mfaCopied, setMfaCopied] = useState(false);

  useEffect(() => {
    getMfaStatus().then(({ enrolled, factorId }) => {
      setMfaEnrolled(enrolled);
      setMfaActiveFactorId(factorId);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentGroupId) fetchLoanableItems(currentGroupId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroupId]);

  useEffect(() => {
    if (!user) return;
    fetchMyOrganizations(user.id);
    fetchMyAdminGroups(user.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleStartMfaEnroll = async () => {
    try {
      setMfaEnrolling(true);
      setMfaConfirmError('');
      const result = await enrollMfa();
      if (!result) { setMfaConfirmError('Kunne ikke starte 2FA-oppsett. Prøv igjen.'); return; }
      setMfaQr(result.qr);
      setMfaSecret(result.secret);
      setMfaEnrollFactorId(result.factorId);
      setMfaConfirmCode('');
      setShowMfaEnroll(true);
    } catch (e: unknown) {
      setMfaConfirmError((e instanceof Error && e.message) ? e.message : 'Noe gikk galt. Prøv igjen.');
    } finally {
      setMfaEnrolling(false);
    }
  };

  const handleConfirmMfaEnroll = async () => {
    if (mfaConfirmCode.length !== 6) return;
    setMfaConfirming(true); setMfaConfirmError('');
    const err = await confirmMfaEnrollment(mfaEnrollFactorId, mfaConfirmCode);
    if (err) { setMfaConfirmError(err); setMfaConfirming(false); return; }
    const status = await getMfaStatus();
    setMfaEnrolled(status.enrolled);
    setMfaActiveFactorId(status.factorId);
    setShowMfaEnroll(false);
    setMfaConfirmCode('');
    setMfaConfirming(false);
  };

  const handleUnenrollMfa = async () => {
    if (!mfaActiveFactorId) return;
    setMfaUnenrolling(true);
    await unenrollMfa(mfaActiveFactorId);
    setMfaEnrolled(false);
    setMfaActiveFactorId(null);
    setShowMfaUnenroll(false);
    setMfaUnenrolling(false);
  };

  const handleChangePassword = async () => {
    setChangePasswordError('');
    if (!currentPassword) { setChangePasswordError('Skriv inn nåværende passord'); return; }
    const pwCheck = isValidPassword(newPassword);
    if (!pwCheck.ok) { setChangePasswordError(pwCheck.message); return; }
    if (newPassword !== newPasswordConfirm) { setChangePasswordError('Passordene stemmer ikke overens'); return; }
    setChangePasswordSaving(true);
    const err = await changePassword(currentPassword, newPassword);
    if (err) { setChangePasswordError(err); setChangePasswordSaving(false); return; }
    setChangePasswordSaving(false);
    setChangePasswordSuccess(true);
    setCurrentPassword(''); setNewPassword(''); setNewPasswordConfirm('');
    setTimeout(() => { setShowChangePassword(false); setChangePasswordSuccess(false); }, 1500);
  };

  useEffect(() => {
    if (currentGroupId) {
      fetchMembers(currentGroupId);
      fetchGroupInvites(currentGroupId);
      fetchGroupOrganization(currentGroupId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroupId]);

  const group = currentGroup();
  const handleCreate = async () => {
    const nameCheck = isValidGroupName(groupName);
    if (!nameCheck.ok) { setError(nameCheck.message); return; }
    setLoading(true); setError('');
    const err = await createGroup(groupName.trim(), groupDesc.trim(), user!.id);
    if (err) { setError(err); }
    else { setShowCreateSheet(false); setGroupName(''); setGroupDesc(''); onJoined(); }
    setLoading(false);
  };

  const handleJoin = async () => {
  if (!inviteCode.trim()) { setError('Skriv inn koden'); return; }
  if (!isValidInviteCode(inviteCode)) { setError('Koden må være 6–8 tegn'); return; }
  setLoading(true); setError('');
  const err = await joinByCode(inviteCode.trim(), user!.id);
  if (err) { setError(err); setLoading(false); return; }
  setShowJoinSheet(false);
  setInviteCode('');
  onJoined();
  setLoading(false);
};

  const handleEditGroup = async () => {
    if (!editName.trim() || !group) return;
    await updateGroup(group.id, { name: editName.trim(), description: editDesc.trim() });
    setShowEditGroup(false);
  };

  const handleUpdateProfile = async () => {
    const nameCheck = isValidName(newFullName);
    if (!nameCheck.ok) return;
    await updateProfile({ full_name: newFullName.trim(), kommune: newKommune.trim() || null, fylke: newFylke || null });
    setShowEditProfile(false);
  };

  const copyInvite = () => {
    if (group?.invite_code) { navigator.clipboard.writeText(group.invite_code); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  const copyOrgInvite = (code: string) => {
    navigator.clipboard.writeText(code);
    setOrgCopied(code);
    setTimeout(() => setOrgCopied(null), 2000);
  };

  const renderOrgCard = (org: Organization) => (
    <Card key={org.id} style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: myAdminOrgIds.has(org.id) ? 10 : 0 }}>
        <div style={{ width: 38, height: 38, borderRadius: 'var(--radius-sm)', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Building2 size={19} color="var(--green-dark)" strokeWidth={1.8} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{org.name}</div>
          {org.type && <div style={{ fontSize: 12, color: 'var(--text2)' }}>{org.type}</div>}
        </div>
        {myAdminOrgIds.has(org.id) ? (
          <button onClick={() => openManageOrg(org.id)} style={{ fontSize: 13, fontWeight: 600, color: 'var(--green-dark)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
            Administrer
          </button>
        ) : (
          <button onClick={() => handleLeaveOrg(org.id, org.name)} style={{ fontSize: 13, fontWeight: 600, color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
            Forlat
          </button>
        )}
      </div>
      {myAdminOrgIds.has(org.id) && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ flex: 1, padding: '8px 10px', background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontFamily: 'monospace', fontSize: 15, fontWeight: 700, letterSpacing: 2, textAlign: 'center' }}>
            {org.invite_code}
          </div>
          <button onClick={() => copyOrgInvite(org.invite_code)} style={{ padding: '8px 10px', borderRadius: 'var(--radius-sm)', background: orgCopied === org.invite_code ? 'var(--green-light)' : 'var(--bg)', border: '1px solid var(--border-strong)', fontSize: 12, fontWeight: 500, color: orgCopied === org.invite_code ? 'var(--green-dark)' : 'var(--text)', flexShrink: 0, cursor: 'pointer' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{orgCopied === org.invite_code ? <><Check size={12} strokeWidth={2.5} /> Kopiert</> : <><Copy size={12} strokeWidth={2} /> Kopier</>}</span>
          </button>
        </div>
      )}
    </Card>
  );

  const handleCreateOrg = async () => {
    if (!user) return;
    if (!orgName.trim()) { setOrgCreateError('Navn er påkrevd'); return; }
    setOrgCreating(true); setOrgCreateError('');
    const err = await createOrganization(orgName.trim(), orgType, user.id);
    setOrgCreating(false);
    if (err) { setOrgCreateError(err); return; }
    setOrgName(''); setOrgType(''); setShowCreateOrgSheet(false);
  };

  const handleJoinOrg = async () => {
    if (!joinOrgCode.trim()) { setJoinOrgError('Skriv inn en kode'); return; }
    setJoinOrgSaving(true); setJoinOrgError('');
    const err = await joinOrganizationByCode(joinOrgCode.trim());
    setJoinOrgSaving(false);
    if (err) { setJoinOrgError(err); return; }
    setJoinOrgCode(''); setShowJoinOrgSheet(false);
    if (user) fetchMyOrganizations(user.id);
  };

  const openManageOrg = (organizationId: string) => {
    setManageOrgId(organizationId);
    setNewOrgAdminEmail(''); setOrgAdminError(''); setLinkGroupId(''); setLinkGroupError('');
    setShowCreateOrgGroup(false); setNewOrgGroupName(''); setNewOrgGroupError('');
    fetchOrgAdminsList(organizationId);
    fetchOrgMembersList(organizationId);
    fetchOrgGroups(organizationId);
  };

  const handleLeaveOrg = async (organizationId: string, orgName: string) => {
    if (!user) return;
    const ok = await confirmOrg(`Forlate organisasjonen «${orgName}»? Du mister samtidig tilgang til eventuelle grupper som tilhører organisasjonen.`, { confirmLabel: 'Forlat organisasjon', danger: true });
    if (!ok) return;
    const err = await leaveOrganization(organizationId, user.id);
    if (err) window.alert(err);
  };

  const handleRemoveOrgMember = async (targetUserId: string, name: string) => {
    if (!manageOrgId) return;
    const ok = await confirmOrg(`Fjerne ${name} fra organisasjonen? Personen mister samtidig tilgang til eventuelle grupper som tilhører organisasjonen.`, { confirmLabel: 'Fjern medlem', danger: true });
    if (!ok) return;
    await removeOrgMember(manageOrgId, targetUserId);
  };

  const handleCreateGroupForOrg = async () => {
    if (!manageOrgId || !user) return;
    if (!newOrgGroupName.trim()) { setNewOrgGroupError('Navn er påkrevd'); return; }
    setNewOrgGroupSaving(true); setNewOrgGroupError('');
    const err = await createGroup(newOrgGroupName.trim(), '', user.id);
    if (err) { setNewOrgGroupSaving(false); setNewOrgGroupError(err); return; }
    const newGroupId = useGroups.getState().currentGroupId;
    if (newGroupId) {
      const linkErr = await linkGroupToOrganization(manageOrgId, newGroupId);
      if (linkErr) { setNewOrgGroupSaving(false); setNewOrgGroupError(linkErr); return; }
    }
    setNewOrgGroupSaving(false);
    setNewOrgGroupName(''); setShowCreateOrgGroup(false);
  };

  const handleUpdateOrgType = async (newType: string) => {
    if (!manageOrgId) return;
    const err = await updateOrganizationType(manageOrgId, newType);
    if (!err) { setOrgTypeSaved(true); setTimeout(() => setOrgTypeSaved(false), 1500); }
  };

  const handleAddOrgAdmin = async () => {
    if (!manageOrgId || !newOrgAdminEmail.trim()) return;
    setOrgAddingAdmin(true); setOrgAdminError('');
    const err = await addOrgAdmin(manageOrgId, newOrgAdminEmail.trim());
    setOrgAddingAdmin(false);
    if (err) { setOrgAdminError(err); return; }
    setNewOrgAdminEmail('');
  };

  const handleRemoveOrgAdmin = async (targetUserId: string, email: string) => {
    if (!manageOrgId) return;
    const ok = await confirmOrg(`Fjerne ${email} som admin for organisasjonen?`, { confirmLabel: 'Fjern', danger: true });
    if (!ok) return;
    await removeOrgAdmin(manageOrgId, targetUserId);
  };

  const handleLinkGroup = async () => {
    if (!manageOrgId || !linkGroupId) return;
    const err = await linkGroupToOrganization(manageOrgId, linkGroupId);
    if (err) { setLinkGroupError(err); return; }
    setLinkGroupId(''); setLinkGroupError('');
  };

  const handleUnlinkGroup = async (groupId: string, groupName: string) => {
    if (!manageOrgId) return;
    const ok = await confirmOrg(`Koble fra «${groupName}»? Gruppen slettes ikke, den knyttes bare ikke lenger til organisasjonen.`, { confirmLabel: 'Koble fra', danger: true });
    if (!ok) return;
    await unlinkGroupFromOrganization(manageOrgId, groupId);
  };

  const handleDeleteOrg = async (organizationId: string, orgName: string) => {
    if (!user) return;
    const ok = await confirmOrg(`Slette organisasjonen «${orgName}»? Grupper og ting som er koblet til blir IKKE slettet, men mister koblingen til organisasjonen. Dette kan ikke angres.`, { confirmLabel: 'Slett organisasjon', danger: true });
    if (!ok) return;
    await deleteOrganization(organizationId, user.id);
    setManageOrgId(null);
  };

  const openHistory = async () => {
    setShowHistory(true);
    await fetchLoanHistory(user!.id);
  };

  const handleDeleteGroup = async () => {
    if (!group) return;
    setDeletingGroup(true); setDeleteGroupError('');
    const err = await deleteGroup(group.id);
    if (err) { setDeleteGroupError(err); setDeletingGroup(false); }
    else setShowDeleteGroup(false);
  };

  const openDeleteAccount = () => {
    setShowDeleteAccount(true);
    const el = document.getElementById('profile-content');
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteAccount = async () => {
    setDeleting(true); setDeleteError('');
    const err = await deleteAccount();
    if (err) { setDeleteError(err); setDeleting(false); }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim() || !group || !profile) return;
    if (!isValidEmail(inviteEmail.trim())) { setInviteError('Ugyldig e-postadresse'); return; }
    setInviteSending(true); setInviteError('');
    const err = await sendInviteEmail(inviteEmail.trim(), profile.full_name, group.name, group.invite_code);
    if (err) setInviteError(err);
    else {
      setInviteSent(true);
      setInviteEmail('');
      fetchGroupInvites(group.id);
      setTimeout(() => { setInviteSent(false); setShowInviteEmail(false); }, 2000);
    }
    setInviteSending(false);
  };
  const handleResendInvite = async (inviteId: string, email: string) => {
    if (!group || !profile) return;
    setResendingId(inviteId); setResendSuccess(null);
    const err = await sendInviteEmail(email, profile.full_name, group.name, group.invite_code);
    if (!err) {
      fetchGroupInvites(group.id);
      setResendSuccess(inviteId);
      setTimeout(() => setResendSuccess(null), 2000);
    }
    setResendingId(null);
  };

  const handleLeaveGroup = async () => {
    if (!group) return;
    setLeaving(true); setLeaveError('');
    const err = await leaveGroup(group.id, user!.id);
    if (err) { setLeaveError(err); setLeaving(false); }
    else setShowLeaveGroup(false);
  };

  const handleRemoveMember = async () => {
  if (!memberToRemove) return;
  const memberToolIds = tools.filter(t => t.owner_id === memberToRemove.userId).map(t => t.id);
  for (const toolId of memberToolIds) {
    await removeToolFromGroup(toolId, currentGroupId!);
  }
  await removeMember(memberToRemove.id);
  setShowRemoveMember(false);
  setMemberToRemove(null);
};

  const isAdmin = members.find(m => m.user_id === user?.id)?.role === 'admin';
  const groupOrg = group ? groupOrgInfo[group.id] : null;
  // For grupper som tilhører en organisasjon: kun styret (org-admin) kan invitere,
  // ikke gruppeadmin/vanlige medlemmer — Roys eksplisitte krav («kan de jo invitere
  // egentlig hvem som helst»). For vanlige grupper er alt uendret (alle medlemmer kan invitere).
  const canInviteToGroup = groupOrg ? myAdminOrgIds.has(groupOrg.id) : true;

  if (!profile) return null;

  return (
    <div style={{ position: 'relative' }}>
      {/* Profile header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <div style={{ flexShrink: 0 }}>
          <Avatar name={profile.full_name} color={profile.avatar_color} size="lg" imageUrl={profile.avatar_url} />
          <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.3 }}>{profile.full_name}</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>{profile.email}</div>
        </div>
        <button onClick={() => { setNewFullName(profile.full_name); setNewKommune(profile.kommune || ''); setNewFylke(profile.fylke || ''); setShowEditProfile(true); }} style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', padding: '6px 12px', fontSize: 13, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 5 }}><Pencil size={13} strokeWidth={2} /> Rediger</button>
      </div>
      {avatarUploading && <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>Laster opp bilde...</div>}
      {avatarError && <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 8 }}>{avatarError}</div>}

      <Divider />

      {/* Profil-fanens seksjoner: Profil / Grupper / Sikkerhet — samme pill-mønster som Mine-siden */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {([
          { key: 'profil' as ProfileTab, label: 'Profil' },
          { key: 'grupper' as ProfileTab, label: 'Grupper' },
          { key: 'sikkerhet' as ProfileTab, label: 'Sikkerhet' },
        ]).map(t => {
          const active = activeProfileTab === t.key;
          return (
            <button key={t.key} onClick={() => setActiveProfileTab(t.key)}
              style={{
                flex: 1, padding: '8px 6px', borderRadius: 20, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${active ? 'var(--green)' : 'var(--border-strong)'}`,
                background: active ? 'var(--green)' : 'var(--surface)',
                color: active ? '#fff' : 'var(--text2)',
              }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {activeProfileTab === 'grupper' && <>

      {/* Hurtighandlinger — øverst i Grupper-fanen */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
        {[
          { label: 'Lånhistorikk', icon: <ClipboardList size={20} color="#2563eb" strokeWidth={1.8} />, bg: '#dbeafe', onClick: openHistory },
          { label: 'Ny gruppe',    icon: <Plus size={20} color="#7c3aed" strokeWidth={2} />,           bg: '#ede9fe', onClick: () => setShowCreateSheet(true) },
          { label: 'Bli med',      icon: <Key size={20} color="#d97706" strokeWidth={1.8} />,          bg: '#fef3c7', onClick: () => setShowJoinSheet(true) },
        ].map(item => (
          <button key={item.label} onClick={item.onClick} style={{ padding: '12px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {item.icon}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', textAlign: 'center', lineHeight: 1.2 }}>{item.label}</span>
          </button>
        ))}
      </div>

      {/* Current group */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <SectionLabel>Aktiv gruppe</SectionLabel>
        {groups.length > 1 && <button onClick={() => setShowGroupSheet(true)} style={{ fontSize: 13, color: 'var(--green)', background: 'none', border: 'none' }}>Bytt gruppe</button>}
      </div>

      {group && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{group.name}</div>
              {group.description && <div style={{ fontSize: 13, color: 'var(--text2)' }}>{group.description}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {isAdmin && <button onClick={() => { setEditName(group.name); setEditDesc(group.description); setShowEditGroup(true); }} style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Pencil size={17} strokeWidth={2} /></button>}
              {group.created_by === user?.id && <button onClick={() => { setDeleteGroupError(''); setDeleteGroupConfirmed(false); setShowDeleteGroup(true); }} style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Trash2 size={17} strokeWidth={2} /></button>}
            </div>
          </div>
          {canInviteToGroup ? (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Invitasjonskode</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <div style={{ flex: 1, padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontFamily: 'monospace', fontSize: 18, fontWeight: 700, letterSpacing: 3, textAlign: 'center' }}>
                  {group.invite_code}
                </div>
                <button onClick={copyInvite} style={{ padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: copied ? 'var(--green-light)' : 'var(--bg)', border: '1px solid var(--border-strong)', fontSize: 13, fontWeight: 500, color: copied ? 'var(--green-dark)' : 'var(--text)', flexShrink: 0, cursor: 'pointer' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>{copied ? <><Check size={13} strokeWidth={2.5} /> Kopiert</> : <><Copy size={13} strokeWidth={2} /> Kopier</>}</span>
                </button>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>Del koden med venner så kan de bli med</div>
            </>
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--text3)', marginBottom: 4, lineHeight: 1.5 }}>
              Kun styret i {groupOrg!.name} kan invitere til denne gruppen.
            </div>
          )}

          {groupOrg && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12.5, color: 'var(--text2)' }}>
              <Building2 size={13} strokeWidth={2} style={{ flexShrink: 0 }} />
              <span>Del av organisasjonen <strong>{groupOrg.name}</strong>{groupOrg.type ? ` (${groupOrg.type})` : ''}</span>
            </div>
          )}

          <Divider />

          {/* Members — collapsible, nå inni samme boks som gruppekortet */}
          <button
            onClick={() => setMembersOpen(o => !o)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', padding: '4px 0 10px', cursor: 'pointer' }}
          >
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              Medlemmer ({members.length})
            </span>
            {membersOpen ? <ChevronUp size={16} color="var(--text3)" strokeWidth={2} /> : <ChevronDown size={16} color="var(--text3)" strokeWidth={2} />}
          </button>

          {membersOpen && members.map(m => {
            const p = m.profile;
            if (!p) return null;
            const isMe = m.user_id === user?.id;
            const memberTools = loanableItems.filter(t => t.user_id === m.user_id);
            const memberAvail = memberTools.filter(t => t.status === 'available').length;
            return (
              <div key={m.id} style={{ padding: '10px 0', borderTop: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Avatar name={p.full_name} color={p.avatar_color} size="md" imageUrl={p.avatar_url} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{p.full_name}{isMe ? ' (deg)' : ''}</div>
                    <div style={{ fontSize: 13, color: 'var(--text2)' }}>{memberAvail} ledig av {memberTools.length} verktøy</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Badge variant={m.role === 'admin' ? 'blue' : memberAvail > 0 ? 'green' : 'amber'}>
                      {m.role === 'admin' ? 'Admin' : memberAvail > 0 ? 'Har ledige' : 'Ingen ledige'}
                    </Badge>
                    {isAdmin && !isMe && (
                      <button
                        onClick={() => { setMemberToRemove({ id: m.id, userId: m.user_id, name: p.full_name }); setShowRemoveMember(true); }}
                        style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      ><X size={16} strokeWidth={2} /></button>
                    )}
                  </div>
                </div>
                {isAdmin && !isMe && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
                    <button onClick={() => updateMemberRole(m.id, m.role === 'admin' ? 'member' : 'admin')} style={{ fontSize: 12, color: 'var(--blue)', background: 'none', border: 'none', cursor: 'pointer' }}>
                      {m.role === 'admin' ? 'Gjør til medlem' : 'Gjør til admin'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Pending invites — collapsible, only for admins */}
          {isAdmin && (
            <>
              <button
                onClick={() => setInvitesOpen(o => !o)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', padding: '4px 0 10px', cursor: 'pointer', marginTop: 8 }}
              >
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  {`Inviterte (${groupInvites.length})`}
                </span>
                {invitesOpen ? <ChevronUp size={16} color="var(--text3)" strokeWidth={2} /> : <ChevronDown size={16} color="var(--text3)" strokeWidth={2} />}
              </button>

              {invitesOpen && (
                groupInvites.length === 0
                  ? <div style={{ fontSize: 13, color: 'var(--text3)', padding: '2px 0 8px' }}>Ingen ventende invitasjoner</div>
                  : groupInvites.map(inv => (
                    <div key={inv.id} style={{ padding: '10px 0', borderTop: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Mail size={16} color="var(--text3)" strokeWidth={1.8} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.email}</div>
                          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                            Invitert {new Date(inv.invited_at).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' })}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button
                            onClick={() => handleResendInvite(inv.id, inv.email)}
                            disabled={resendingId === inv.id}
                            title="Send på nytt"
                            style={{ background: resendSuccess === inv.id ? 'var(--green-light)' : 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: resendSuccess === inv.id ? 'var(--green-dark)' : 'var(--text2)' }}
                          >
                            {resendSuccess === inv.id ? <><Check size={13} strokeWidth={2.5} /> Sendt</> : resendingId === inv.id ? '...' : <><RefreshCw size={13} strokeWidth={2} /> Send igjen</>}
                          </button>
                          <button
                            onClick={() => deleteGroupInvite(inv.id)}
                            title="Slett invitasjon"
                            style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 4 }}
                          ><Trash2 size={15} strokeWidth={1.8} /></button>
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </>
          )}

          {/* Inviter på e-post + Destruktiv handling — nå inni samme boks som gruppekortet, rett under Inviterte */}
          <Divider />
          {canInviteToGroup && (
            <button onClick={() => { setShowInviteEmail(true); setInviteError(''); setInviteSent(false); }} style={{ width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', marginBottom: 10 }}>
              <Mail size={17} color="#fff" strokeWidth={2} />
              <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Inviter på e-post</span>
            </button>
          )}
          <button onClick={() => { setLeaveError(''); setShowLeaveGroup(true); }} style={{ width: '100%', padding: '11px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--red-light, #fecaca)', background: '#fff5f5', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left' }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <LogOut size={17} color="var(--red)" strokeWidth={1.8} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--red)' }}>Forlat gruppe</div>
              <div style={{ fontSize: 12, color: 'var(--red)', opacity: 0.7, marginTop: 1 }}>Du mister tilgang til gruppen</div>
            </div>
            <ChevronRight size={16} color="var(--red)" strokeWidth={2} style={{ flexShrink: 0, opacity: 0.6 }} />
          </button>
        </Card>
      )}

      {/* Organisasjon (borettslag/vel) — rett under gruppekortet, viser ALLE
          organisasjoner du er del av (ikke bare den aktive gruppens) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, marginTop: 6 }}>
        <SectionLabel>Organisasjon</SectionLabel>
      </div>

      {organizations.map(org => renderOrgCard(org))}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button onClick={() => { setOrgName(''); setOrgType(''); setOrgCreateError(''); setShowCreateOrgSheet(true); }} style={{ flex: 1, padding: '11px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', fontSize: 13, fontWeight: 500, color: 'var(--text)', cursor: 'pointer' }}>
          Opprett organisasjon
        </button>
        <button onClick={() => { setJoinOrgCode(''); setJoinOrgError(''); setShowJoinOrgSheet(true); }} style={{ flex: 1, padding: '11px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', fontSize: 13, fontWeight: 500, color: 'var(--text)', cursor: 'pointer' }}>
          Bli med i organisasjon
        </button>
      </div>

      </>}

      {activeProfileTab === 'profil' && <>

      <button onClick={() => setShowHelp(true)} style={{ width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
        <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-sm)', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <BookOpen size={17} color="var(--green-dark)" strokeWidth={1.8} />
        </div>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={{ fontWeight: 600 }}>Brukerveiledning</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 1 }}>Slik bruker du Toolshare.no</div>
        </div>
        <ChevronRight size={16} color="var(--text3)" strokeWidth={2} />
      </button>

      <button onClick={() => setShowPersonvern(true)} style={{ width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
        <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-sm)', background: 'var(--blue-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <ShieldCheck size={17} color="var(--blue)" strokeWidth={1.8} />
        </div>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={{ fontWeight: 600 }}>Personvern</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 1 }}>Personvernerklæring og cookies</div>
        </div>
        <ChevronRight size={16} color="var(--text3)" strokeWidth={2} />
      </button>

      {/* Annonseinnstillinger — to atskilte valg: Google (lovpålagt cookie-samtykke)
          og Toolshare sine egne sponsorannonser (ingen sporingscookies, rent
          støtte-/inntektsvalg). Plassert her (ikke Sikkerhet) siden dette handler
          om personvern/appoppførsel, ikke kontosikkerhet. Samme kort-stil som
          Brukerveiledning/Personvern-knappene over. */}
      <button onClick={reopenBanner} style={{ width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
        <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-sm)', background: 'var(--blue-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Megaphone size={17} color="var(--blue)" strokeWidth={1.8} />
        </div>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={{ fontWeight: 600 }}>Google-annonser</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 1 }}>
            {adConsent === 'granted' ? 'Godtatt' : adConsent === 'denied' ? 'Avslått' : 'Ikke besvart'} — trykk for å endre
          </div>
        </div>
        <ChevronRight size={16} color="var(--text3)" strokeWidth={2} />
      </button>

      <div style={{ padding: '13px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-sm)', background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Heart size={17} color="var(--accent)" strokeWidth={1.8} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Sponsorannonser</div>
          </div>
          <button
            onClick={() => setSponsorConsent(!sponsorConsent)}
            style={{ width: 40, height: 22, borderRadius: 12, background: sponsorConsent ? 'var(--green)' : 'var(--border-strong)', border: 'none', cursor: 'pointer', position: 'relative', flexShrink: 0, padding: 0 }}
          >
            <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: sponsorConsent ? 20 : 2, transition: 'left 0.15s' }} />
          </button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5, marginTop: 8 }}>
          Disse annonsene bruker ingen sporingscookies. Vises de og du klikker på dem, hjelper det med å holde Toolshare levende og gratis.
        </div>
      </div>

      <button onClick={signOut} style={{ width: '100%', padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--red-light)', background: 'none', fontSize: 13, fontWeight: 500, color: 'var(--red)' }}>
        Logg ut
      </button>

      <button
        onClick={onOpenFeedback}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', marginTop: 12, padding: '12px', borderRadius: 12, background: 'var(--bg)', border: '1.5px solid var(--border-strong)', color: 'var(--text2)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
      >
        💬 Send tilbakemelding
      </button>

      <div style={{ textAlign: 'center', marginTop: 16, marginBottom: 8, fontSize: 12, color: 'var(--text3)' }}>
        Toolshare v{packageJson.version}
      </div>

      </>}

      {activeProfileTab === 'sikkerhet' && <>

      <SectionLabel>Sikkerhet</SectionLabel>
      <button onClick={() => { setShowChangePassword(true); setChangePasswordError(''); setChangePasswordSuccess(false); setCurrentPassword(''); setNewPassword(''); setNewPasswordConfirm(''); }} style={{ width: '100%', padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        <Key size={15} strokeWidth={2} /> Bytt passord
      </button>
      {mfaConfirmError && !showMfaEnroll && <ErrorMsg msg={mfaConfirmError} />}
      {!mfaEnrolled && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--amber-light)', border: '1px solid var(--amber)', marginBottom: 8 }}>
          <AlertTriangle size={15} color="var(--amber)" strokeWidth={2} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: 'var(--amber)', lineHeight: 1.4 }}>Kontoen din er ikke beskyttet med 2-faktor autentisering.</span>
        </div>
      )}
      {mfaEnrolled ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--green)', background: 'var(--green-light)', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldCheck size={16} color="var(--green-dark)" strokeWidth={2.2} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--green-dark)' }}>2-faktor er aktivert</span>
          </div>
          <button onClick={() => setShowMfaUnenroll(true)} style={{ padding: '6px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--red-light)', background: 'none', fontSize: 12, fontWeight: 600, color: 'var(--red)', cursor: 'pointer' }}>
            Deaktiver
          </button>
        </div>
      ) : (
        <button onClick={handleStartMfaEnroll} disabled={mfaEnrolling} style={{ width: '100%', padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: mfaEnrolling ? 0.6 : 1 }}>
          <ShieldCheck size={15} strokeWidth={2} /> {mfaEnrolling ? 'Setter opp…' : 'Aktiver 2-faktor autentisering'}
        </button>
      )}

      {/* Betrodde enheter */}
      {mfaEnrolled && trustedDevices.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <button
            onClick={() => setDevicesOpen(o => !o)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', padding: '4px 0 10px', cursor: 'pointer' }}
          >
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              Betrodde enheter ({trustedDevices.length})
            </span>
            {devicesOpen ? <ChevronUp size={16} color="var(--text3)" strokeWidth={2} /> : <ChevronDown size={16} color="var(--text3)" strokeWidth={2} />}
          </button>
          {devicesOpen && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {trustedDevices.map(dev => {
                  const isMobile = /iOS|Android/.test(dev.device_name);
                  const isCurrent = dev.id === currentDeviceId;
                  return (
                    <Card key={dev.id} style={{ border: isCurrent ? '1px solid var(--green)' : undefined }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {isMobile ? <Smartphone size={15} color="var(--text3)" strokeWidth={2} style={{ flexShrink: 0 }} /> : <Monitor size={15} color="var(--text3)" strokeWidth={2} style={{ flexShrink: 0 }} />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            {dev.device_name}
                            {isCurrent && <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--green)', background: 'var(--green-light)', padding: '1px 6px', borderRadius: 99 }}>Denne</span>}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Sist brukt {formatRelative(dev.last_used_at)}</div>
                        </div>
                        <button onClick={() => revokeDevice(dev.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text3)', display: 'flex', alignItems: 'center' }}>
                          <Trash2 size={14} strokeWidth={2} />
                        </button>
                      </div>
                    </Card>
                  );
                })}
              </div>
              <button onClick={revokeAllDevices} style={{ width: '100%', marginTop: 8, padding: '8px 0', borderRadius: 'var(--radius-sm)', border: '1px solid var(--red-light)', background: 'none', fontSize: 12, fontWeight: 500, color: 'var(--red)', cursor: 'pointer' }}>
                Slett alle betrodde enheter
              </button>
            </>
          )}
        </div>
      )}

      </>}

      {/* Edit profile sheet */}
      {showEditProfile && (
        <BottomSheet title="Rediger profil" onClose={() => setShowEditProfile(false)}>
          {/* Profilbilde */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <Avatar name={profile.full_name} color={profile.avatar_color} size="lg" imageUrl={profile.avatar_url} />
              <button
                onClick={() => avatarInputRef.current?.click()}
                disabled={avatarUploading}
                style={{ position: 'absolute', bottom: -2, right: -2, width: 24, height: 24, borderRadius: '50%', background: 'var(--green)', border: '2px solid var(--surface-solid)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
              >
                <Camera size={12} color="#fff" strokeWidth={2.5} />
              </button>
              <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => avatarInputRef.current?.click()}
                disabled={avatarUploading}
                style={{ padding: '6px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', fontSize: 13, fontWeight: 500, color: 'var(--text)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <Camera size={13} strokeWidth={2} /> {avatarUploading ? 'Laster opp...' : 'Last opp bilde'}
              </button>
              {profile.avatar_url && (
                <button
                  onClick={handleDeleteAvatar}
                  disabled={avatarUploading}
                  style={{ padding: '6px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--red-light)', background: 'none', fontSize: 13, fontWeight: 500, color: 'var(--red)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
                >
                  <Trash2 size={13} strokeWidth={2} /> Slett bilde
                </button>
              )}
            </div>
            {avatarError && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 8, textAlign: 'center' }}>{avatarError}</div>}
          </div>

          {/* Avatar-figurer */}
          <Field label="Velg avatar">
            {(() => {
              const visibleAvatars = ['🦊','🐻','🐼','🦁','🐯','🐨','🐸','🐺','🦝','🐙'];
              const moreAvatars = [
                // Mennesker — herre (lys → mørk)
                '👨🏻','👨🏼','👨🏽','👨🏾','👨🏿',
                // Mennesker — dame (lys → mørk)
                '👩🏻','👩🏼','👩🏽','👩🏾','👩🏿',
                // Gutt
                '👦🏻','👦🏼','👦🏽','👦🏾','👦🏿',
                // Jente
                '👧🏻','👧🏼','👧🏽','👧🏾','👧🏿',
                // Eldre
                '👴🏻','👴🏿','👵🏻','👵🏿',
                // Fantasy
                '🧙','🧚','🦸','🧝','🧜','🧛','🧞','🦹','🧌','🧟','👸','🤴',
                // Sci-Fi & Spill
                '👾','🤖','👽','🦾','🧬','🪐',
                // Humor & Moro
                '🤡','👻','💀','😈','🃏','🎭',
                // Roller
                '👮','👷','🥷','🤠','🕵','🧑‍🚀',
              ];
              const hasActiveInMore = moreAvatars.some(e => profile.avatar_url === `preset:${e}`);

              const EmojiBtn = ({ emoji, fromExpanded }: { emoji: string; fromExpanded: boolean }) => {
                const val = `preset:${emoji}`;
                const active = profile.avatar_url === val;
                return (
                  <button onClick={async () => { await deleteStorageImage(profile.avatar_url); updateProfile({ avatar_url: val }); if (fromExpanded) setShowMoreAvatars(false); }}
                    style={{ width: 52, height: 52, borderRadius: '50%', border: `3px solid ${active ? 'var(--green)' : 'var(--border)'}`, background: active ? 'var(--green-light)' : 'var(--bg)', cursor: 'pointer', fontSize: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: active ? '0 0 0 1px var(--green)' : 'none', flexShrink: 0 }}>
                    {emoji}
                  </button>
                );
              };

              return (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                    {visibleAvatars.map(e => <EmojiBtn key={e} emoji={e} fromExpanded={false} />)}
                  </div>

                  {showMoreAvatars && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                      {moreAvatars.map(e => <EmojiBtn key={e} emoji={e} fromExpanded={true} />)}
                    </div>
                  )}

                  <button onClick={() => setShowMoreAvatars(v => !v)} style={{ marginTop: 10, width: '100%', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-xs)', padding: '7px 0', fontSize: 13, color: 'var(--text2)', cursor: 'pointer' }}>
                    {showMoreAvatars ? '▴ Vis færre' : `▾ Vis flere${hasActiveInMore ? ' (aktiv)' : ''}`}
                  </button>
                </>
              );
            })()}
          </Field>

          {/* Fullt navn */}
          <Field label="Fullt navn"><Input value={newFullName} onChange={e => setNewFullName(e.target.value)} /></Field>

          {/* Sted (valgfritt) — brukes til å målrette sponsor-annonser lokalt */}
          <Field label="Fylke (valgfritt)">
            <Select value={newFylke} onChange={e => { setNewFylke(e.target.value); setNewKommune(''); }}>
              <option value="">Ikke oppgitt</option>
              {FYLKER.map(f => <option key={f} value={f}>{f}</option>)}
            </Select>
          </Field>
          <Field label="Kommune (valgfritt)">
            <Select value={newKommune} onChange={e => setNewKommune(e.target.value)} disabled={!newFylke}>
              <option value="">{newFylke ? 'Velg kommune' : 'Velg fylke først'}</option>
              {(KOMMUNER_BY_FYLKE[newFylke] || []).map(k => <option key={k} value={k}>{k}</option>)}
            </Select>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 5, lineHeight: 1.4 }}>
              Brukes kun til å vise deg relevante, lokale annonser i appen — deles ikke med sponsorer eller andre. Se{' '}
              <button
                type="button"
                onClick={() => { setShowEditProfile(false); setShowPersonvern(true); }}
                style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--green-dark)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
              >
                Personvern
              </button>
              {' '}for detaljer.
            </div>
          </Field>

          <PrimaryBtn onClick={handleUpdateProfile}>Lagre</PrimaryBtn>
          <SecondaryBtn onClick={() => setShowEditProfile(false)}>Avbryt</SecondaryBtn>

          {/* Slett konto — gjemt nederst */}
          <button
            onClick={() => { setShowEditProfile(false); setTimeout(() => openDeleteAccount(), 100); }}
            style={{ width: '100%', padding: 12, marginTop: 8, borderRadius: 'var(--radius-sm)', border: 'none', background: 'none', fontSize: 13, color: 'var(--text3)', textDecoration: 'underline', cursor: 'pointer' }}
          >
            Slett konto
          </button>
        </BottomSheet>
      )}

      {/* Edit group sheet */}
      {showEditGroup && (
        <BottomSheet title="Rediger gruppe" onClose={() => setShowEditGroup(false)}>
          <Field label="Gruppenavn"><Input value={editName} onChange={e => setEditName(e.target.value)} autoFocus /></Field>
          <Field label="Beskrivelse"><Textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={2} /></Field>
          <PrimaryBtn onClick={handleEditGroup}>Lagre</PrimaryBtn>
          <SecondaryBtn onClick={() => setShowEditGroup(false)}>Avbryt</SecondaryBtn>
        </BottomSheet>
      )}

      {/* Loan history sheet */}
      {showHistory && (
        <BottomSheet title="Lånhistorikk" onClose={() => setShowHistory(false)}>
          {loanHistory.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text3)', padding: '20px 0' }}>Ingen historikk enda</p>}
          {loanHistory.map(h => (
            <div key={h.id} style={{ display: 'flex', gap: 10, marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 24 }}>{h.tool?.emoji}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{h.tool?.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                  {h.borrower?.full_name} lånte fra {h.owner?.full_name}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                  {new Date(h.loaned_at).toLocaleDateString('nb-NO')}
                  {h.returned_at ? ` → ${new Date(h.returned_at).toLocaleDateString('nb-NO')}` : ' → Ikke levert'}
                </div>
              </div>
              <Badge variant={h.returned_at ? 'green' : 'amber'}>{h.returned_at ? 'Levert' : 'Aktiv'}</Badge>
            </div>
          ))}
          <SecondaryBtn onClick={() => setShowHistory(false)}>Lukk</SecondaryBtn>
        </BottomSheet>
      )}

      {/* Switch group */}
      {showGroupSheet && (() => {
        // Seksjonerer etter groupOrgInfo (per-gruppe org-tilknytning) i stedet for
        // brukerens EGNE organisasjoner — fungerer dermed også for grupper eid av en
        // organisasjon brukeren selv kun er vanlig gruppemedlem i (ikke org-medlem).
        const personalGroups = groups.filter(g => !groupOrgInfo[g.id]);
        const orgSectionsMap = new Map<string, { label: string; items: typeof groups }>();
        groups.forEach(g => {
          const org = groupOrgInfo[g.id];
          if (!org) return;
          if (!orgSectionsMap.has(org.id)) orgSectionsMap.set(org.id, { label: org.name, items: [] });
          orgSectionsMap.get(org.id)!.items.push(g);
        });
        const groupSections: { label: string; isOrg: boolean; items: typeof groups }[] = [
          { label: 'Dine grupper', isOrg: false, items: personalGroups },
          ...Array.from(orgSectionsMap.values()).map(s => ({ ...s, isOrg: true })),
        ];
        return (
          <BottomSheet title="Velg gruppe" onClose={() => setShowGroupSheet(false)}>
            {groupSections.filter(s => s.items.length > 0).map(section => (
              <div key={section.label} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', marginBottom: 8, borderRadius: 30, background: 'var(--green-light)', width: 'fit-content', fontSize: 11.5, fontWeight: 700, color: 'var(--green-dark)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {section.isOrg ? <Building2 size={12} strokeWidth={2.5} /> : <Users size={12} strokeWidth={2.5} />}
                  {section.label}
                </div>
                {section.items.map(g => (
                  <div key={g.id} onClick={() => { setCurrentGroup(g.id); setShowGroupSheet(false); }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 14, border: `1px solid ${g.id === currentGroupId ? 'var(--green)' : 'var(--border)'}`, borderRadius: 'var(--radius-sm)', marginBottom: 8, cursor: 'pointer', background: g.id === currentGroupId ? 'var(--green-light)' : 'var(--surface)' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{g.name}</div>
                      {g.description && <div style={{ fontSize: 13, color: 'var(--text2)' }}>{g.description}</div>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {g.id === currentGroupId && <Check size={18} color="var(--green)" strokeWidth={2.5} />}
                      {g.created_by === user?.id ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentGroup(g.id);
                            setShowGroupSheet(false);
                            setDeleteGroupError('');
                            setDeleteGroupConfirmed(false);
                            setShowDeleteGroup(true);
                          }}
                          style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', borderRadius: 8 }}
                          aria-label={`Slett ${g.name}`}
                        >
                          <Trash2 size={16} color="var(--red)" strokeWidth={2} />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentGroup(g.id);
                            setShowGroupSheet(false);
                            setLeaveError('');
                            setShowLeaveGroup(true);
                          }}
                          style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', borderRadius: 8 }}
                          aria-label={`Forlat ${g.name}`}
                        >
                          <LogOut size={16} color="var(--amber)" strokeWidth={2} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
            <SecondaryBtn onClick={() => setShowGroupSheet(false)}>Lukk</SecondaryBtn>
          </BottomSheet>
        );
      })()}

      {/* Create group */}
      {showCreateSheet && (
        <BottomSheet title="Opprett ny gruppe" onClose={() => setShowCreateSheet(false)}>
          {error && <ErrorMsg msg={error} />}
          <Field label="Gruppenavn"><Input placeholder="F.eks. Nabolaget" value={groupName} onChange={e => setGroupName(e.target.value)} autoFocus /></Field>
          <Field label="Beskrivelse (valgfritt)"><Textarea placeholder="Hvem er med..." value={groupDesc} onChange={e => setGroupDesc(e.target.value)} rows={2} /></Field>
          <PrimaryBtn onClick={handleCreate} disabled={loading}>{loading ? 'Oppretter...' : 'Opprett'}</PrimaryBtn>
          <SecondaryBtn onClick={() => setShowCreateSheet(false)}>Avbryt</SecondaryBtn>
        </BottomSheet>
      )}

      {/* Delete group sheet */}
      {showDeleteGroup && group && (
        <BottomSheet title="Slett gruppe" onClose={() => setShowDeleteGroup(false)}>
          {deleteGroupError && <ErrorMsg msg={deleteGroupError} />}
          {groupsLoading ? (
            <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--text2)', fontSize: 14 }}>Laster gruppedata...</div>
          ) : (
            <>
              <div style={{ background: 'var(--red-light)', borderRadius: 'var(--radius-sm)', padding: 14, marginBottom: 18 }}>
                <div style={{ fontWeight: 600, color: 'var(--red)', marginBottom: 6 }}>⚠️ Dette kan ikke angres!</div>
                <div style={{ fontSize: 14, color: 'var(--red)', lineHeight: 1.6 }}>
                  Kategorier, lånhistorikk og kommentarer i <strong>«{group.name}»</strong> slettes permanent for alle medlemmer.
                  {tools.length > 0 && (
                    <> <strong>{tools.length} verktøy registrert direkte i gruppen</strong> slettes også permanent. (Ting fra Inventar som kun er delt med gruppen forblir trygt i eierens eget inventar.)</>
                  )}
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', borderRadius: 'var(--radius-sm)', border: `1px solid ${deleteGroupConfirmed ? 'var(--red)' : 'var(--border-strong)'}`, background: deleteGroupConfirmed ? 'var(--red-light)' : 'var(--bg)', marginBottom: 16, cursor: 'pointer' }}>
                <input type="checkbox" checked={deleteGroupConfirmed} onChange={e => setDeleteGroupConfirmed(e.target.checked)} style={{ marginTop: 2, flexShrink: 0, width: 16, height: 16, accentColor: 'var(--red)' }} />
                <span style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5 }}>
                  Ja, jeg forstår at <strong>«{group.name}»</strong> slettes permanent og dette ikke kan angres.
                </span>
              </label>
              <button onClick={handleDeleteGroup} disabled={!deleteGroupConfirmed || deletingGroup} style={{ width: '100%', padding: 13, background: 'var(--red)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 600, cursor: deleteGroupConfirmed ? 'pointer' : 'not-allowed', opacity: (!deleteGroupConfirmed || deletingGroup) ? 0.5 : 1 }}>
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>{deletingGroup ? 'Sletter...' : <><Trash2 size={15} strokeWidth={2} /> Slett «{group.name}»</>}</span>
              </button>
            </>
          )}
          <SecondaryBtn onClick={() => setShowDeleteGroup(false)}>Avbryt</SecondaryBtn>
        </BottomSheet>
      )}

      {/* Delete account */}
      {showDeleteAccount && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}>
          <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 24, width: '100%', maxWidth: 400 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--red-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}><Trash2 size={26} color="var(--red)" strokeWidth={2} /></div>
            <h2 style={{ fontSize: 18, fontWeight: 700, textAlign: 'center', marginBottom: 8 }}>Slett konto</h2>
            {deleteError && <ErrorMsg msg={deleteError} />}
            <div style={{ background: 'var(--red-light)', borderRadius: 'var(--radius-sm)', padding: 12, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, color: 'var(--red)', marginBottom: 4, fontSize: 14 }}>⚠️ Dette kan ikke angres!</div>
              <div style={{ fontSize: 13, color: 'var(--red)', lineHeight: 1.5 }}>
                Alle dine verktøy, lånhistorikk, kommentarer og gruppemedlemskap slettes permanent.
              </div>
            </div>
            <button onClick={handleDeleteAccount} disabled={deleting} style={{ width: '100%', padding: 13, background: 'var(--red)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: deleting ? 0.7 : 1, marginBottom: 10 }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>{deleting ? 'Sletter...' : <><Trash2 size={15} strokeWidth={2} /> Ja, slett kontoen min</>}</span>
            </button>
            <button onClick={() => setShowDeleteAccount(false)} style={{ width: '100%', padding: 13, background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 500, cursor: 'pointer' }}>
              Avbryt
            </button>
          </div>
        </div>
      )}

      {/* Invite by email */}
      {showInviteEmail && (
        <BottomSheet title="Inviter på e-post" onClose={() => setShowInviteEmail(false)}>
          {inviteError && <ErrorMsg msg={inviteError} />}
          {inviteSent && <div style={{ background: 'var(--green-light)', color: 'var(--green-dark)', padding: '10px 13px', borderRadius: 'var(--radius-sm)', fontSize: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}><Check size={14} strokeWidth={2.5} /> Invitasjon sendt!</div>}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Invitasjonskode for «{group?.name}»</div>
            <div style={{ padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontFamily: 'monospace', fontSize: 18, fontWeight: 700, letterSpacing: 3, textAlign: 'center' }}>
              {group?.invite_code}
            </div>
          </div>
          <Field label="E-postadresse til mottaker">
            <Input type="email" placeholder="ola@eksempel.no" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} autoFocus />
          </Field>
          <PrimaryBtn onClick={handleSendInvite} disabled={inviteSending}><span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>{inviteSending ? 'Sender...' : <><Mail size={15} strokeWidth={2} /> Send invitasjon</>}</span></PrimaryBtn>
          <SecondaryBtn onClick={() => setShowInviteEmail(false)}>Avbryt</SecondaryBtn>
        </BottomSheet>
      )}



      {/* Join group */}
      {showJoinSheet && (
        <BottomSheet title="Bli med i gruppe" onClose={() => setShowJoinSheet(false)}>
          {error && <ErrorMsg msg={error} />}
          <Field label="Invitasjonskode">
            <Input placeholder="F.eks. ABC123" value={inviteCode} onChange={e => setInviteCode(e.target.value.toUpperCase())} style={{ textTransform: 'uppercase', letterSpacing: 4, fontSize: 20, textAlign: 'center' }} />
          </Field>
          <PrimaryBtn onClick={handleJoin} disabled={loading}>{loading ? 'Kobler til...' : 'Bli med'}</PrimaryBtn>
          <SecondaryBtn onClick={() => setShowJoinSheet(false)}>Avbryt</SecondaryBtn>
        </BottomSheet>
      )}

      {showCreateOrgSheet && (
        <BottomSheet title="Opprett organisasjon" onClose={() => setShowCreateOrgSheet(false)}>
          {orgCreateError && <ErrorMsg msg={orgCreateError} />}
          <Field label="Navn"><Input placeholder="F.eks. Solsiden Borettslag" value={orgName} onChange={e => setOrgName(e.target.value)} autoFocus /></Field>
          <Field label="Type (valgfritt)">
            <Select value={orgType} onChange={e => setOrgType(e.target.value)}>
              <option value="">Ikke oppgitt</option>
              <option value="Borettslag">Borettslag</option>
              <option value="Vel">Vel</option>
              <option value="Sameie">Sameie</option>
              <option value="Annet">Annet</option>
            </Select>
          </Field>
          <PrimaryBtn onClick={handleCreateOrg} disabled={orgCreating}>{orgCreating ? 'Oppretter...' : 'Opprett'}</PrimaryBtn>
          <SecondaryBtn onClick={() => setShowCreateOrgSheet(false)}>Avbryt</SecondaryBtn>
        </BottomSheet>
      )}

      {showJoinOrgSheet && (
        <BottomSheet title="Bli med i organisasjon" onClose={() => setShowJoinOrgSheet(false)}>
          {joinOrgError && <ErrorMsg msg={joinOrgError} />}
          <Field label="Delingskode">
            <Input placeholder="F.eks. ABC123" value={joinOrgCode} onChange={e => setJoinOrgCode(e.target.value.toUpperCase())} style={{ textTransform: 'uppercase', letterSpacing: 4, fontSize: 20, textAlign: 'center' }} />
          </Field>
          <PrimaryBtn onClick={handleJoinOrg} disabled={joinOrgSaving}>{joinOrgSaving ? 'Kobler til...' : 'Bli med'}</PrimaryBtn>
          <SecondaryBtn onClick={() => setShowJoinOrgSheet(false)}>Avbryt</SecondaryBtn>
        </BottomSheet>
      )}

      {manageOrgId && (() => {
        const org = organizations.find(o => o.id === manageOrgId);
        const linkedGroups = orgGroups[manageOrgId] || [];
        const linkableGroups = myAdminGroups.filter(g => !linkedGroups.some(lg => lg.id === g.id));
        return (
          <BottomSheet title={`Administrer ${org?.name || 'organisasjon'}`} onClose={() => setManageOrgId(null)}>
            <Field label="Type">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Select value={org?.type || ''} onChange={e => handleUpdateOrgType(e.target.value)} style={{ flex: 1 }}>
                  <option value="">Ikke oppgitt</option>
                  <option value="Borettslag">Borettslag</option>
                  <option value="Vel">Vel</option>
                  <option value="Sameie">Sameie</option>
                  <option value="Annet">Annet</option>
                </Select>
                {orgTypeSaved && <Check size={18} color="var(--green)" strokeWidth={2.5} />}
              </div>
            </Field>

            <SectionLabel>Admin (styre)</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              {orgAdminsList.map(a => (
                <div key={a.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--bg)' }}>
                  <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.email}</span>
                  <button onClick={() => handleRemoveOrgAdmin(a.user_id, a.email)} title="Fjern admin"
                    style={{ padding: '4px 7px', borderRadius: 30, border: '1px solid #fca5a5', background: 'none', color: 'var(--red)', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <X size={12} strokeWidth={2.5} />
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
              <Input type="email" placeholder="E-postadresse" value={newOrgAdminEmail} onChange={e => setNewOrgAdminEmail(e.target.value)} style={{ flex: 1 }} />
              <button onClick={handleAddOrgAdmin} disabled={orgAddingAdmin}
                style={{ padding: '0 16px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--green)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <UserPlus size={15} strokeWidth={2} /> Legg til
              </button>
            </div>
            {orgAdminError && <ErrorMsg msg={orgAdminError} />}

            <SectionLabel>Medlemmer</SectionLabel>
            {(() => {
              const regularMembers = orgMembersList.filter(m => !orgAdminsList.some(a => a.user_id === m.user_id));
              return regularMembers.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20 }}>Ingen vanlige medlemmer ennå.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
                  {regularMembers.map(m => (
                    <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--bg)' }}>
                      <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.profile?.full_name || 'Ukjent'}</span>
                      <button onClick={() => handleRemoveOrgMember(m.user_id, m.profile?.full_name || 'medlemmet')} title="Fjern fra organisasjonen"
                        style={{ padding: '4px 7px', borderRadius: 30, border: '1px solid #fca5a5', background: 'none', color: 'var(--red)', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <X size={12} strokeWidth={2.5} />
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: showGroupHelpText ? 8 : 10 }}>
              <SectionLabel>Tilkoblede grupper</SectionLabel>
              <button onClick={() => setShowGroupHelpText(s => !s)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'var(--text3)', marginBottom: 10 }}>
                <HelpCircle size={15} strokeWidth={2} />
              </button>
            </div>
            {showGroupHelpText && (
              <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.5, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', marginBottom: 14 }}>
                <strong>Opprett gruppe</strong> lager en helt ny gruppe som automatisk hører til organisasjonen.<br />
                <strong>Koble til eksisterende gruppe</strong> (lenger ned) er for når du allerede har en gruppe fra før som du vil knytte til organisasjonen i etterkant.
              </div>
            )}

            {showCreateOrgGroup ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                <Input placeholder="Navn på ny gruppe" value={newOrgGroupName} onChange={e => setNewOrgGroupName(e.target.value)} autoFocus />
                {newOrgGroupError && <ErrorMsg msg={newOrgGroupError} />}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={handleCreateGroupForOrg} disabled={newOrgGroupSaving}
                    style={{ flex: 1, padding: '9px 0', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--green)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {newOrgGroupSaving ? 'Oppretter...' : 'Opprett'}
                  </button>
                  <button onClick={() => { setShowCreateOrgGroup(false); setNewOrgGroupName(''); setNewOrgGroupError(''); }}
                    style={{ flex: 1, padding: '9px 0', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text2)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                    Avbryt
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowCreateOrgGroup(true)}
                style={{ width: '100%', padding: '11px 14px', marginBottom: 14, borderRadius: 'var(--radius-sm)', border: '1px dashed var(--border-strong)', background: 'none', fontSize: 14, fontWeight: 500, color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Plus size={15} strokeWidth={2} /> Opprett gruppe
              </button>
            )}

            {linkedGroups.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '10px 0', color: 'var(--text3)', fontSize: 13, marginBottom: 14 }}>Ingen grupper tilkoblet ennå</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                {linkedGroups.map(g => (
                  <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--bg)' }}>
                    <span style={{ flex: 1, fontSize: 13 }}>{g.name}</span>
                    <button onClick={() => handleUnlinkGroup(g.id, g.name)} title="Koble fra"
                      style={{ padding: '4px 7px', borderRadius: 30, border: '1px solid #fca5a5', background: 'none', color: 'var(--red)', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      <X size={12} strokeWidth={2.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {linkableGroups.length > 0 && (
              <div style={{ display: 'flex', gap: 6 }}>
                <Select value={linkGroupId} onChange={e => setLinkGroupId(e.target.value)} style={{ flex: 1 }}>
                  <option value="">Velg en gruppe du er admin i...</option>
                  {linkableGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </Select>
                <button onClick={handleLinkGroup} disabled={!linkGroupId}
                  style={{ padding: '0 16px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--green)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                  Koble til
                </button>
              </div>
            )}
            {linkGroupError && <ErrorMsg msg={linkGroupError} />}

            <Divider />
            <button
              onClick={() => org && handleDeleteOrg(org.id, org.name)}
              style={{ width: '100%', padding: '11px 0', borderRadius: 'var(--radius-sm)', border: '1px solid #fca5a5', background: 'none', color: 'var(--red)', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 10 }}
            >
              Slett organisasjon
            </button>
            <SecondaryBtn onClick={() => setManageOrgId(null)}>Lukk</SecondaryBtn>
          </BottomSheet>
        );
      })()}
      {orgConfirmDialog}

      {showLeaveGroup && group && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}>
          <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 24, width: '100%', maxWidth: 400 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--amber-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}><LogOut size={26} color="var(--amber)" strokeWidth={2} /></div>
            <h2 style={{ fontSize: 18, fontWeight: 700, textAlign: 'center', marginBottom: 8 }}>Forlat gruppe</h2>
            {groupsLoading ? (
              <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text2)', fontSize: 14 }}>Laster gruppedata...</div>
            ) : (
              <>
                <p style={{ fontSize: 14, color: 'var(--text2)', textAlign: 'center', marginBottom: 16 }}>
                  Er du sikker på at du vil forlate <strong>«{group.name}»</strong>?
                </p>
                {leaveError && <div style={{ background: 'var(--amber-light)', color: 'var(--amber)', padding: '10px 13px', borderRadius: 'var(--radius-sm)', fontSize: 14, marginBottom: 14 }}>⚠️ {leaveError}</div>}
                <button onClick={handleLeaveGroup} disabled={leaving} style={{ width: '100%', padding: 13, background: 'var(--red)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 600, cursor: 'pointer', marginBottom: 10, opacity: leaving ? 0.7 : 1 }}>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>{leaving ? 'Forlater...' : <><LogOut size={15} strokeWidth={2} /> Ja, forlat gruppen</>}</span>
                </button>
              </>
            )}
            <button onClick={() => setShowLeaveGroup(false)} style={{ width: '100%', padding: 13, background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 500, cursor: 'pointer' }}>
              Avbryt
            </button>
          </div>
        </div>
      )}

      {showRemoveMember && memberToRemove && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}>
          <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 24, width: '100%', maxWidth: 400 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--red-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}><UserMinus size={26} color="var(--red)" strokeWidth={2} /></div>
            <h2 style={{ fontSize: 18, fontWeight: 700, textAlign: 'center', marginBottom: 8 }}>Fjern medlem</h2>
            <p style={{ fontSize: 14, color: 'var(--text2)', textAlign: 'center', marginBottom: 20 }}>
              Er du sikker på at du vil fjerne <strong>{memberToRemove.name}</strong> fra gruppen?
            </p>
            <button
              onClick={handleRemoveMember}
              style={{ width: '100%', padding: 13, background: 'var(--red)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 600, cursor: 'pointer', marginBottom: 10 }}
            >
              Ja, fjern fra gruppen
            </button>
            <button
              onClick={() => { setShowRemoveMember(false); setMemberToRemove(null); }}
              style={{ width: '100%', padding: 13, background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 500, cursor: 'pointer' }}
            >
              Avbryt
            </button>
          </div>
        </div>
      )}

      {showHelp && <HelpPage onClose={() => setShowHelp(false)} onOpenFeedback={() => { setShowHelp(false); onOpenFeedback(); }} />}
      {showPersonvern && <PersonvernPage onClose={() => setShowPersonvern(false)} />}

      {/* MFA enrollment sheet */}
      {showMfaEnroll && (
        <BottomSheet title="Aktiver 2-faktor autentisering" onClose={() => setShowMfaEnroll(false)}>
          <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
            Skann QR-koden med Google Authenticator eller Authy, og skriv inn den 6-sifrede koden for å bekrefte.
          </p>
          {mfaQr && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <img src={mfaQr} alt="QR-kode for 2FA" style={{ width: 180, height: 180, borderRadius: 8, border: '1px solid var(--border)', background: '#fff' }} />
            </div>
          )}
          <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', padding: '10px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Manuell nøkkel</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: 'var(--text)', letterSpacing: 2, flex: 1, wordBreak: 'break-all' }}>{mfaSecret}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(mfaSecret); setMfaCopied(true); setTimeout(() => setMfaCopied(false), 2000); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green)', padding: 4 }}
              >
                {mfaCopied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </div>
          {mfaConfirmError && <ErrorMsg msg={mfaConfirmError} />}
          <Field label="Bekreft kode fra appen">
            <Input
              type="tel"
              inputMode="numeric"
              placeholder="123456"
              value={mfaConfirmCode}
              onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 6); setMfaConfirmCode(v); if (v.length === 6) setTimeout(() => handleConfirmMfaEnroll(), 0); }}
              style={{ fontFamily: 'monospace', fontSize: 22, letterSpacing: 8, textAlign: 'center' }}
            />
          </Field>
          <PrimaryBtn onClick={handleConfirmMfaEnroll} disabled={mfaConfirming || mfaConfirmCode.length !== 6}>
            {mfaConfirming ? 'Bekrefter…' : 'Bekreft og aktiver'}
          </PrimaryBtn>
        </BottomSheet>
      )}

      {/* MFA unenroll confirmation */}
      {showMfaUnenroll && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', padding: 24 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 16, padding: 24, width: '100%', maxWidth: 340 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <ShieldOff size={20} color="var(--red)" strokeWidth={2} />
              <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Deaktiver 2-faktor?</span>
            </div>
            <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 20 }}>
              Kontoen din vil bare være beskyttet av passord. Du kan aktivere 2FA igjen når som helst.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={handleUnenrollMfa}
                disabled={mfaUnenrolling}
                style={{ padding: 13, background: 'var(--red)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: mfaUnenrolling ? 0.6 : 1 }}
              >
                {mfaUnenrolling ? 'Deaktiverer…' : 'Ja, deaktiver'}
              </button>
              <button
                onClick={() => setShowMfaUnenroll(false)}
                style={{ padding: 13, background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 500, cursor: 'pointer' }}
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {showChangePassword && (
        <BottomSheet title="Bytt passord" onClose={() => setShowChangePassword(false)}>
          {changePasswordError && <ErrorMsg msg={changePasswordError} />}
          {changePasswordSuccess ? (
            <div style={{ background: 'var(--green-light)', color: 'var(--green-dark)', padding: '10px 13px', borderRadius: 'var(--radius-sm)', fontSize: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Check size={14} strokeWidth={2.5} /> Passordet er byttet!
            </div>
          ) : (
            <>
              <Field label="Nåværende passord">
                <Input type="password" placeholder="Ditt passord i dag" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} autoFocus />
              </Field>
              <Field label="Nytt passord">
                <Input type="password" placeholder="Minst 8 tegn med tall og bokstav" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              </Field>
              <Field label="Bekreft nytt passord">
                <Input type="password" placeholder="Gjenta det nye passordet" value={newPasswordConfirm} onChange={e => setNewPasswordConfirm(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleChangePassword()} />
              </Field>
              <PrimaryBtn onClick={handleChangePassword} disabled={changePasswordSaving}>{changePasswordSaving ? 'Lagrer…' : 'Bytt passord'}</PrimaryBtn>
              <SecondaryBtn onClick={() => setShowChangePassword(false)}>Avbryt</SecondaryBtn>
            </>
          )}
        </BottomSheet>
      )}
    </div>
  );
};